import { format, parse } from 'date-fns';
import { ArrowDownToLine, ArrowUpFromLine, DollarSign, MessageSquare, Zap } from 'lucide-react';
import { useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { StatCard } from '../components/StatCard';
import { useChatOverview, useCostAnalytics } from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';

const PROVIDER_COLORS: Record<string, string> = {
  openai: 'hsl(160, 60%, 45%)',
  anthropic: 'hsl(25, 85%, 55%)',
  google: 'hsl(217, 91%, 60%)',
};

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(225, 20%, 16%)',
  border: '1px solid hsl(225, 15%, 25%)',
  borderRadius: '8px',
  color: 'hsl(0, 0%, 95%)',
};

const GRID_STROKE = 'hsl(225, 15%, 18%)';
const AXIS_STROKE = 'hsl(0, 0%, 45%)';
const PRIMARY_COLOR = 'hsl(184, 92%, 56%)';

function generateMonthOptions() {
  return Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: format(new Date(2024, i), 'MMMM'),
  }));
}

function generateYearOptions() {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 3 }, (_, i) => currentYear - i);
}

function formatExternalUsd(value: string) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return '$0.00';
  return `$${n.toFixed(n > 0 && n < 0.01 ? 4 : 2)}`;
}

function ChartSkeleton() {
  return (
    <div className="h-64 flex items-center justify-center">
      <div className="text-text-muted">Loading...</div>
    </div>
  );
}

export function Costs() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const { data, isLoading } = useCostAnalytics({ month, year });
  const { data: chatOverview, isLoading: chatOverviewLoading } = useChatOverview();

  const dailyChartData =
    data?.dailyCosts.map((d) => ({
      date: format(parse(d.date, 'yyyy-MM-dd', new Date()), 'MMM d'),
      cost: parseFloat(d.costUsd),
      executions: d.executionCount,
    })) ?? [];

  const modelChartData =
    data?.byModel.map((m) => ({
      model: m.model,
      cost: parseFloat(m.costUsd),
    })) ?? [];

  const providerChartData =
    data?.byProvider.map((p) => ({
      provider: p.provider,
      cost: parseFloat(p.costUsd),
    })) ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Cost Analytics</h1>
        <div className="flex items-center gap-2">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
          >
            {generateMonthOptions().map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
          >
            {generateYearOptions().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Cost"
          value={data ? `$${parseFloat(data.totalCostUsd).toFixed(2)}` : '$0.00'}
          icon={DollarSign}
          loading={isLoading}
        />
        <StatCard
          label="Total Executions"
          value={data?.totalExecutions ?? 0}
          icon={Zap}
          loading={isLoading}
        />
        <StatCard
          label="Input Tokens"
          value={data?.totalInputTokens ?? 0}
          icon={ArrowDownToLine}
          loading={isLoading}
        />
        <StatCard
          label="Output Tokens"
          value={data?.totalOutputTokens ?? 0}
          icon={ArrowUpFromLine}
          loading={isLoading}
        />
      </div>

      {/* Cost by Surface (FLAG-3): chat surface = orchestrator LLM cost ONLY, from
          agent_executions. WhatsApp send cost lives in a SEPARATE ledger
          (message_logs) and is deliberately NOT summed into this figure — see
          costNote below, sourced from the backend to avoid drift. */}
      <div className="bg-surface rounded-xl border border-border p-5">
        <h2 className="text-sm font-medium text-text-secondary mb-4">Cost by Surface</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
          <StatCard
            label="Chat Surface (LLM)"
            value={chatOverview ? `$${chatOverview.costUsd}` : '$0.00'}
            icon={MessageSquare}
            loading={chatOverviewLoading}
          />
        </div>
        {chatOverview?.costNote && (
          <p className="text-xs text-text-muted italic px-1">{chatOverview.costNote}</p>
        )}
      </div>

      {data?.externalCosts && (
        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">External Service Costs</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <StatCard
              label="External Total"
              value={formatExternalUsd(data.externalCosts.totalCostUsd)}
              icon={DollarSign}
              loading={isLoading}
            />
            <StatCard
              label="External Entries"
              value={data.externalCosts.entryCount}
              icon={Zap}
              loading={isLoading}
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xs font-medium text-text-muted mb-2">By Service</h3>
              {data.externalCosts.byService.length === 0 ? (
                <p className="text-sm text-text-muted">No external costs this period</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-text-muted">
                        <th className="pb-2 pr-4 font-medium">Provider</th>
                        <th className="pb-2 pr-4 font-medium">Service</th>
                        <th className="pb-2 pr-4 font-medium">Units</th>
                        <th className="pb-2 font-medium">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.externalCosts.byService.map((entry) => (
                        <tr
                          key={`${entry.provider}-${entry.service}`}
                          className="border-b border-border last:border-b-0"
                        >
                          <td className="py-2 pr-4 text-text-secondary">{entry.provider}</td>
                          <td className="py-2 pr-4 font-mono text-xs text-text">{entry.service}</td>
                          <td className="py-2 pr-4 text-text-secondary">{entry.units}</td>
                          <td className="py-2 text-text-secondary">
                            {formatExternalUsd(entry.costUsd)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div>
              <h3 className="text-xs font-medium text-text-muted mb-2">By Lead</h3>
              {data.externalCosts.byLead.length === 0 ? (
                <p className="text-sm text-text-muted">
                  No lead-attributed external costs this period
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-text-muted">
                        <th className="pb-2 pr-4 font-medium">Lead</th>
                        <th className="pb-2 pr-4 font-medium">Entries</th>
                        <th className="pb-2 font-medium">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.externalCosts.byLead.map((entry) => (
                        <tr key={entry.leadId} className="border-b border-border last:border-b-0">
                          <td className="py-2 pr-4 text-text-secondary">
                            {entry.leadName ?? entry.leadId}
                          </td>
                          <td className="py-2 pr-4 text-text-secondary">{entry.entryCount}</td>
                          <td className="py-2 text-text-secondary">
                            {formatExternalUsd(entry.costUsd)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border p-5">
        <h2 className="text-sm font-medium text-text-secondary mb-4">Daily Costs</h2>
        {isLoading ? (
          <ChartSkeleton />
        ) : dailyChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyChartData}>
              <defs>
                <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={PRIMARY_COLOR} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={PRIMARY_COLOR} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
              <XAxis dataKey="date" stroke={AXIS_STROKE} fontSize={12} />
              <YAxis stroke={AXIS_STROKE} fontSize={12} tickFormatter={(v) => `$${v}`} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(value) => [`$${(Number(value) || 0).toFixed(4)}`, 'Cost']}
              />
              <Area
                type="monotone"
                dataKey="cost"
                stroke={PRIMARY_COLOR}
                strokeWidth={2}
                fill="url(#costGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-text-muted">
            No data for this period
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Cost by Model</h2>
          {isLoading ? (
            <ChartSkeleton />
          ) : modelChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={264}>
              <BarChart data={modelChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis
                  type="number"
                  stroke={AXIS_STROKE}
                  fontSize={12}
                  tickFormatter={(v) => `$${v}`}
                />
                <YAxis
                  type="category"
                  dataKey="model"
                  stroke={AXIS_STROKE}
                  fontSize={11}
                  width={140}
                />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value) => [`$${(Number(value) || 0).toFixed(4)}`, 'Cost']}
                />
                <Bar dataKey="cost" fill={PRIMARY_COLOR} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
        </div>

        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Cost by Provider</h2>
          {isLoading ? (
            <ChartSkeleton />
          ) : providerChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={264}>
              <PieChart>
                <Pie
                  data={providerChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  dataKey="cost"
                  nameKey="provider"
                  paddingAngle={2}
                  label={({ name, value }) => `${name ?? ''} ($${Number(value).toFixed(2)})`}
                >
                  {providerChartData.map((entry) => (
                    <Cell
                      key={entry.provider}
                      fill={PROVIDER_COLORS[entry.provider.toLowerCase()] ?? PRIMARY_COLOR}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(value) => [`$${(Number(value) || 0).toFixed(4)}`, 'Cost']}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
        </div>
      </div>

      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-medium text-text-secondary">Top Organizations by Cost</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-muted">
                <th className="px-5 py-3 font-medium">Organization</th>
                <th className="px-5 py-3 font-medium">Cost</th>
                <th className="px-5 py-3 font-medium">Executions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-border animate-pulse">
                    {Array.from({ length: 3 }).map((_, j) => (
                      <td key={j} className="px-5 py-3">
                        <div className="h-4 bg-border rounded w-20" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data?.topOrganizations?.length ? (
                data.topOrganizations.map((org) => (
                  <tr
                    key={org.id}
                    className="border-b border-border hover:bg-surface-hover transition-colors cursor-pointer"
                    onClick={() => navigate(`/organizations/${org.id}`)}
                  >
                    <td className="px-5 py-3 text-text font-medium">{org.name}</td>
                    <td className="px-5 py-3 text-text-secondary">
                      ${parseFloat(org.costUsd).toFixed(2)}
                    </td>
                    <td className="px-5 py-3 text-text-secondary">
                      {org.executionCount.toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-5 py-8 text-center text-text-muted">
                    No organizations with costs this period
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
